import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { 
  DollarSign, TrendingUp, TrendingDown, PiggyBank, Calendar, 
  ArrowUpRight, ArrowDownRight, Percent, ShieldAlert, AlertTriangle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '@/utils/errorHandler';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function FinanceDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/finance/dashboard`, {
        params: { start_date: startDate, end_date: endDate }
      });
      setData(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load dashboard'));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR'
    }).format(amount || 0);
  };

  const categoryLabels = {
    fuel: 'Fuel',
    maintenance: 'Maintenance',
    insurance: 'Insurance',
    toll: 'Toll Fees',
    salary: 'Salaries',
    office: 'Office',
    vehicle: 'Vehicle',
    other: 'Other'
  };

  const categoryColors = {
    fuel: 'bg-amber-500',
    maintenance: 'bg-blue-500',
    insurance: 'bg-purple-500',
    toll: 'bg-green-500',
    salary: 'bg-pink-500',
    office: 'bg-cyan-500',
    vehicle: 'bg-orange-500',
    other: 'bg-gray-500'
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="finance-dashboard">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Finance Dashboard</h1>
            <p className="text-muted-foreground mt-1">Income vs Expenses Overview</p>
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-40"
            />
            <span className="text-muted-foreground">to</span>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-40"
            />
            <Button onClick={fetchDashboard} disabled={loading}>
              {loading ? 'Loading...' : 'Apply'}
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-green-50 border-green-200">
            <CardHeader className="pb-2">
              <CardDescription className="text-green-700">Income (Excl VAT)</CardDescription>
              <CardTitle className="text-2xl text-green-700 flex items-center gap-2">
                <ArrowUpRight className="h-5 w-5" />
                {formatCurrency(data?.total_income_net)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-green-600">{data?.income_count || 0} transactions</p>
              <p className="text-xs text-green-500">VAT: {formatCurrency(data?.total_income_vat)}</p>
            </CardContent>
          </Card>

          <Card className="bg-red-50 border-red-200">
            <CardHeader className="pb-2">
              <CardDescription className="text-red-700">Expenses (Excl VAT)</CardDescription>
              <CardTitle className="text-2xl text-red-700 flex items-center gap-2">
                <ArrowDownRight className="h-5 w-5" />
                {formatCurrency(data?.total_expenses_net)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-red-600">{data?.expense_count || 0} entries</p>
              <p className="text-xs text-red-500">VAT: {formatCurrency(data?.total_expenses_vat)}</p>
            </CardContent>
          </Card>

          <Card className={data?.net_profit >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-orange-50 border-orange-200'}>
            <CardHeader className="pb-2">
              <CardDescription className={data?.net_profit >= 0 ? 'text-emerald-700' : 'text-orange-700'}>
                Net Profit/Loss
              </CardDescription>
              <CardTitle className={`text-2xl flex items-center gap-2 ${data?.net_profit >= 0 ? 'text-emerald-700' : 'text-orange-700'}`}>
                {data?.net_profit >= 0 ? <TrendingUp className="h-5 w-5" /> : <TrendingDown className="h-5 w-5" />}
                {formatCurrency(data?.net_profit)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className={`text-sm ${data?.net_profit >= 0 ? 'text-emerald-600' : 'text-orange-600'}`}>
                {data?.total_income_net > 0 
                  ? `${((data?.net_profit / data?.total_income_net) * 100).toFixed(1)}% margin`
                  : 'No income recorded'}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-blue-50 border-blue-200">
            <CardHeader className="pb-2">
              <CardDescription className="text-blue-700">VAT Payable</CardDescription>
              <CardTitle className={`text-2xl flex items-center gap-2 ${data?.vat_payable >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
                <Percent className="h-5 w-5" />
                {formatCurrency(data?.vat_payable)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-blue-600">Output VAT - Input VAT</p>
            </CardContent>
          </Card>
        </div>

        {/* DANGER ZONE INDICATOR */}
        {data?.net_profit < 0 && (
          <Alert variant="destructive" className="bg-red-50 border-red-500 border-2" data-testid="danger-zone-alert">
            <ShieldAlert className="h-5 w-5" />
            <AlertTitle className="text-red-800 font-bold flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              DANGER ZONE - Operating at a Loss
            </AlertTitle>
            <AlertDescription className="text-red-700">
              <p className="mb-2">
                For the period <strong>{startDate}</strong> to <strong>{endDate}</strong>, your business is showing a 
                <strong className="text-red-800"> net loss of {formatCurrency(Math.abs(data?.net_profit))}</strong>.
              </p>
              <div className="flex flex-wrap gap-4 mt-3">
                <div className="bg-white/60 rounded px-3 py-2">
                  <p className="text-xs text-red-600">Income</p>
                  <p className="font-bold text-green-700">{formatCurrency(data?.total_income_net)}</p>
                </div>
                <div className="bg-white/60 rounded px-3 py-2">
                  <p className="text-xs text-red-600">Expenses</p>
                  <p className="font-bold text-red-700">{formatCurrency(data?.total_expenses_net)}</p>
                </div>
                <div className="bg-white/60 rounded px-3 py-2">
                  <p className="text-xs text-red-600">Shortfall</p>
                  <p className="font-bold text-red-800">{formatCurrency(Math.abs(data?.net_profit))}</p>
                </div>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Expenses Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PiggyBank className="h-5 w-5" />
              Expenses by Category
            </CardTitle>
            <CardDescription>
              Period: {startDate} to {endDate}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {data?.expenses_by_category && Object.keys(data.expenses_by_category).length > 0 ? (
              <div className="space-y-4">
                {/* Bar Chart */}
                <div className="h-8 flex rounded-lg overflow-hidden">
                  {Object.entries(data.expenses_by_category).map(([cat, amount]) => {
                    const percentage = (amount / data.total_expenses_net) * 100;
                    return (
                      <div
                        key={cat}
                        className={`${categoryColors[cat] || 'bg-gray-500'} flex items-center justify-center text-white text-xs font-medium`}
                        style={{ width: `${percentage}%` }}
                        title={`${categoryLabels[cat] || cat}: ${formatCurrency(amount)}`}
                      >
                        {percentage > 10 && `${percentage.toFixed(0)}%`}
                      </div>
                    );
                  })}
                </div>

                {/* Legend */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                  {Object.entries(data.expenses_by_category)
                    .sort((a, b) => b[1] - a[1])
                    .map(([cat, amount]) => (
                      <div key={cat} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                        <div className={`h-3 w-3 rounded-full ${categoryColors[cat] || 'bg-gray-500'}`} />
                        <div className="flex-1">
                          <p className="text-sm font-medium">{categoryLabels[cat] || cat}</p>
                          <p className="text-lg font-bold">{formatCurrency(amount)}</p>
                          <p className="text-xs text-muted-foreground">
                            {((amount / data.total_expenses_net) * 100).toFixed(1)}%
                          </p>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <PiggyBank className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No expense data for this period</p>
                <p className="text-sm">Add expenses or import bank statements to see breakdown</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <Calendar className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Period Days</p>
                <p className="text-2xl font-bold">
                  {Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24))}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <DollarSign className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Avg Daily Expense</p>
                <p className="text-2xl font-bold">
                  {formatCurrency((data?.total_expenses_net || 0) / Math.max(1, Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24))))}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <ArrowUpRight className="h-8 w-8 mx-auto mb-2 text-green-600" />
                <p className="text-sm text-muted-foreground">Income Txns</p>
                <p className="text-2xl font-bold">{data?.income_count || 0}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <ArrowDownRight className="h-8 w-8 mx-auto mb-2 text-red-600" />
                <p className="text-sm text-muted-foreground">Expense Entries</p>
                <p className="text-2xl font-bold">{data?.expense_count || 0}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
